import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, effect, signal, WritableSignal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { TemporaryStorageService } from '../../../../zentility/_shared/_zen-legacy-common/zen-common-services/_services/common/temporary-storage.service';
import { BillFile, OnboardingContext, BillImportResponse } from '../../../models';
import { BillService } from '../../../services/bill.service';
import { OnboardingNavigationService } from '../../../services/onboarding-navigation.service';
import { PortfolioHelperService } from '../../../../zentility/_modules/portfolio/_services/_helpers/portfolio-helper.service';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { BillAiFlowStepHeaderComponent } from '../bill-ai-flow-step-header/bill-ai-flow-step-header.component';
import { BillAiFlowMigratePromptComponent } from '../bill-ai-flow-migrate-prompt/bill-ai-flow-migrate-prompt.component';
import { BillAiFlowIntroStepComponent } from '../bill-ai-flow-intro-step/bill-ai-flow-intro-step.component';
import { BillAiFlowCustomerStepComponent } from '../bill-ai-flow-customer-step/bill-ai-flow-customer-step.component';
import { BillAiFlowLenStepComponent } from '../bill-ai-flow-len-step/bill-ai-flow-len-step.component';
import { BillAiFlowSaStepComponent } from '../bill-ai-flow-sa-step/bill-ai-flow-sa-step.component';
import { BillAiFlowMeterStepComponent } from '../bill-ai-flow-meter-step/bill-ai-flow-meter-step.component';
import { BillAiFlowBillReviewStepComponent } from '../bill-ai-flow-bill-review-step/bill-ai-flow-bill-review-step.component';
import { BillAiFlowBillOverviewComponent } from '../bill-ai-flow-bill-overview/bill-ai-flow-bill-overview.component';
import { BillAiFlowSummaryStepComponent } from '../bill-ai-flow-summary-step/bill-ai-flow-summary-step.component';
import { SharedModule } from '../../../../zentility/_shared/shared.module';
import { JenZenImgType } from '../../../../zentility/_shared/_components/jen-zen-img/jen-zen-img.component';
import { ZenColors } from '../../../../zentility/_shared/_enums/zen-colors.enum';

@Component({
  selector: 'app-bill-ai-flow-shell',
  standalone: true,
  imports: [
    CommonModule,
    NgxExtendedPdfViewerModule,
    BillAiFlowStepHeaderComponent,
    BillAiFlowMigratePromptComponent,
    BillAiFlowIntroStepComponent,
    BillAiFlowCustomerStepComponent,
    BillAiFlowLenStepComponent,
    BillAiFlowSaStepComponent,
    BillAiFlowMeterStepComponent,
    BillAiFlowBillReviewStepComponent,
    BillAiFlowBillOverviewComponent,
    BillAiFlowSummaryStepComponent,
    SharedModule
  ],
  providers: [BillAiFlowStore],
  templateUrl: './bill-ai-flow-shell.component.html',
  styleUrl: './bill-ai-flow-shell.component.scss'
})
export class BillAiFlowShellComponent implements OnInit, OnDestroy {
  @Input() bills: BillFile[] = [];
  @Input() context!: OnboardingContext;

  @Output() billAiFlowComplete = new EventEmitter<BillImportResponse>();
  @Output() billAiFlowCancelled = new EventEmitter<void>();

  JenZenImgType = JenZenImgType;
  ZenColors = ZenColors;

  // PDF State
  pdfSrc: string | ArrayBuffer | Uint8Array | null = null;
  pdfZoom: number | string = 'page-width';
  pdfCollapsed = true; // Start collapsed — only relevant during bill-review
  pdfLoading = false;
  pdfError: string | null = null;

  // Dual bill PDF tab state
  activePdfTab: WritableSignal<'utility' | 'supplier'> = signal('utility');
  isDualBill = false;
  utilityFileName = '';
  supplierFileName = '';

  private tempFileSub?: Subscription;

  // Resizable Panel State
  pdfPanelWidth: number | null = null;
  isResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  public state = inject(BillAiFlowStore);
  private billService = inject(BillService);
  private onboardingNavSvc = inject(OnboardingNavigationService);
  private tempStorageSvc = inject(TemporaryStorageService);
  private portfolioHelperSvc = inject(PortfolioHelperService);

  constructor() {
    // Watch for close requests (e.g. user deleted the last entity).
    // Deferred via setTimeout to avoid emitting during the signal notification cycle.
    effect(() => {
      if (this.state.closeRequested()) {
        setTimeout(() => {
          this.state.clearPersistedState();
          this.billService.clearBills();
          this.billAiFlowCancelled.emit();
        });
      }
    });

    // Auto-load PDF during bill-review step
    effect(() => {
      const step = this.state.currentStep();
      const billFile = this.state.getCurrentBillFile();
      const previewBill = this.state.getPreviewBillFile();
      const tab = this.activePdfTab();

      if (step === 'bill-review') {
        // Check if current nav entry is a dual bill
        const nav = this.state.getCurrentBillNav();
        this.isDualBill = !!nav?.isDualBill;
        const mainBillFile = (nav?.billFileId ? this.state.getBillFile(nav.billFileId) : null) ?? billFile;
        const pairedBillFile = nav?.pairedBillFileId ? this.state.getBillFile(nav.pairedBillFileId) : null;
        // Same-session pairs: main file = utility, paired = supplier.
        // Scenario C (paired with an already-imported bill): the paired file is
        // the existing utility document and the main file is the supplier bill.
        const utilityBillFile = nav?.pairedFileIsUtility ? pairedBillFile : mainBillFile;
        const supplierBillFile = nav?.pairedFileIsUtility ? mainBillFile : pairedBillFile;
        this.utilityFileName = utilityBillFile?.file?.name || 'Utility Bill';
        this.supplierFileName = supplierBillFile?.file?.name || 'Supplier Bill';

        if (this.isDualBill) {
          // Load PDF based on active tab
          const targetBill = tab === 'supplier' ? supplierBillFile : utilityBillFile;
          if (targetBill) {
            this.loadPdf(targetBill);
          }
        } else {
          if (billFile) {
            this.loadPdf(billFile);
          }
          // Reset tab when switching to non-dual bill
          this.activePdfTab.set('utility');
        }
        this.pdfCollapsed = false;
      } else if (previewBill) {
        // Other steps with a preview (e.g. summary if we ever add it there)
        this.loadPdf(previewBill);
        this.pdfCollapsed = false;
        this.isDualBill = false;
      } else {
        this.pdfCollapsed = true;
        this.pdfSrc = null;
        this.pdfLoading = false;
        this.pdfError = null;
        this.isDualBill = false;
      }
    });
  }

  ngOnInit(): void {
    this.state.initFromContext(this.context, this.bills);
  }

  ngOnDestroy(): void {
    this.tempFileSub?.unsubscribe();
  }

  cancel(): void {
    if (this.state.importSuccess()) {
      // Import already succeeded — clean up session before closing
      this.state.clearPersistedState();
      this.billService.clearBills();
      this.onboardingNavSvc.notifySessionClosed(this.context);
    }
    this.billAiFlowCancelled.emit();
  }

  onImportComplete(response: BillImportResponse): void {
    // Clear persisted bill AI flow state on successful import
    this.state.clearPersistedState();
    // Clear all in-memory state from the root-scoped BillService singleton.
    // This MUST happen before notifySessionClosed(), which tells the parent to
    // close the onboarding tab — destroying the dashboard before it can handle
    // the billAiFlowComplete event and call clearBills() itself.
    this.billService.clearBills();
    // Notify parent components to close the onboarding session tab
    this.onboardingNavSvc.notifySessionClosed(this.context);
    this.billAiFlowComplete.emit(response);
    // Refresh portfolio snapshot + tables so newly-imported customers/LENs/SAs/meters
    // appear in counts immediately, mirroring the manual-add helpers (DEV-14634).
    this.portfolioHelperSvc.refreshCurrentPortfolioPage(null, null, null);
  }

  // --- PDF Methods ---

  switchPdfTab(tab: 'utility' | 'supplier'): void {
    this.activePdfTab.set(tab);
  }

  private loadPdf(bill: BillFile): void {
    this.pdfError = null;
    this.pdfLoading = false;
    this.tempFileSub?.unsubscribe();

    if (bill.fileData?.length) {
      this.pdfSrc = bill.fileData;
    } else if (bill.previewUrl) {
      this.pdfSrc = bill.previewUrl;
    } else if (bill.file?.size > 0) {
      // File exists in memory but no blob URL — create one on-the-fly
      const url = URL.createObjectURL(bill.file);
      bill.previewUrl = url;
      this.pdfSrc = url;
    } else if (bill.tempFileId) {
      // Local data lost — fetch from server-side temp storage
      this.pdfLoading = true;
      this.pdfSrc = null;
      this.fetchTempFile(bill);
    } else {
      console.error('[BillAiFlowShell] Bill has no displayable data:', {
        billId: bill.id, hasFileData: !!bill.fileData, hasPreviewUrl: !!bill.previewUrl,
        hasFile: !!bill.file, tempFileId: bill.tempFileId, status: bill.status
      });
      this.pdfSrc = null;
      this.pdfError = 'Bill file is no longer available. Please re-upload the bill.';
    }
  }

  private fetchTempFile(bill: BillFile): void {
    this.tempFileSub = this.tempStorageSvc.getFileData(bill.tempFileId!).pipe(take(1)).subscribe({
      next: (buffer) => {
        bill.fileData = new Uint8Array(buffer);
        bill.previewUrl = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
        this.pdfSrc = bill.fileData;
        this.pdfLoading = false;
      },
      error: (err) => {
        console.error('[BillAiFlowShell] Failed to fetch temp file', bill.tempFileId, err);
        this.pdfLoading = false;
        this.pdfSrc = null;
        this.pdfError = err.status === 404
          ? 'Bill file has expired. Navigate back to re-upload the bill.'
          : 'Failed to load bill PDF. Navigate to the next bill and back to retry.';
      }
    });
  }

  zoomIn(): void {
    if (typeof this.pdfZoom === 'number') {
      this.pdfZoom = Math.min(this.pdfZoom + 25, 400);
    } else {
      this.pdfZoom = 125;
    }
  }

  zoomOut(): void {
    if (typeof this.pdfZoom === 'number') {
      this.pdfZoom = Math.max(this.pdfZoom - 25, 25);
    } else {
      this.pdfZoom = 75;
    }
  }

  resetZoom(): void {
    this.pdfZoom = 'page-width';
  }

  // --- Resize Handle Logic ---

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.resizeStartX = event.clientX;

    const pdfPanel = (event.target as HTMLElement).parentElement?.querySelector('.pdf-panel') as HTMLElement;
    if (pdfPanel) {
      this.resizeStartWidth = pdfPanel.offsetWidth;
    } else {
      this.resizeStartWidth = this.pdfPanelWidth || 500;
    }

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  @HostListener('document:mousemove', ['$event'])
  onResizeMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    // PDF is on the RIGHT, so dragging left increases width
    const delta = this.resizeStartX - event.clientX;
    const newWidth = Math.max(250, Math.min(window.innerWidth * 0.7, this.resizeStartWidth + delta));
    this.pdfPanelWidth = newWidth;
  }

  @HostListener('document:mouseup')
  onResizeEnd(): void {
    if (this.isResizing) {
      this.isResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }
}
